import { useEffect } from 'react';
import { useApi } from './useApi';
import { api } from './useAuth';
import { applyDisplayDefaults, type DisplayDefaults } from './useDisplaySettings';

/**
 * Applies the per-location display defaults configured in the portal
 * (location.app_config.displayDefaults, hierarchy-merged server-side: site →
 * building → floor → appartement, most specific wins) on launch / when the
 * building config changes. Live changes arrive via the `display_settings`
 * command (see useMqttCommands), which asks for a refetch — see below.
 *
 * The building config endpoint returns `{ success, data: { building: {...} } }`;
 * useApi unwraps the envelope, so we read `data.building.displayDefaults`.
 */
let currentBuildingId: number | undefined;

export function useBuildingDisplayDefaults(buildingId: number | undefined) {
  currentBuildingId = buildingId;
  const { data } = useApi<{ building?: { displayDefaults?: DisplayDefaults } }>(
    buildingId ? `/api/v1/building/${buildingId}/config` : null,
  );

  useEffect(() => {
    const dd = data?.building?.displayDefaults;
    if (dd) applyDisplayDefaults(dd);
  }, [data]);
}

/**
 * Re-read this tablet's own resolved display defaults. A portal push at a site reaches
 * every appartement below it; applying the pushed values directly would overwrite an
 * appartement's own override, so the push only says "refetch" and each tablet resolves
 * its own chain. Returns false when it could not (no building yet, network) — the caller
 * then falls back to the pushed values.
 */
export async function refetchDisplayDefaults(): Promise<boolean> {
  if (!currentBuildingId) return false;
  try {
    const res = (await api.get(`/api/v1/building/${currentBuildingId}/config`)) as {
      data?: { building?: { displayDefaults?: DisplayDefaults } };
      building?: { displayDefaults?: DisplayDefaults };
    } | null;
    const dd = res?.data?.building?.displayDefaults ?? res?.building?.displayDefaults;
    if (!dd) return false;
    applyDisplayDefaults(dd);
    return true;
  } catch {
    return false;
  }
}
